import { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, Bot, User, Loader2, Maximize2, Minimize2, Save, History, Trash2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface SavedConversation {
  id: string;
  title: string;
  messages: Message[];
  created_at: string;
  updated_at: string;
}

// Simple markdown parser for chat messages
const formatMessage = (text: string) => {
  const parts = text.split(/(```[\s\S]*?```)/g);
  
  return parts.map((part, index) => {
    if (part.startsWith("```") && part.endsWith("```")) {
      const code = part.slice(3, -3).replace(/^\w+\n/, "");
      return (
        <pre key={index} className="bg-background/50 rounded p-2 my-2 text-xs overflow-x-auto">
          <code>{code}</code>
        </pre>
      );
    }
    
    const lines = part.split("\n");
    return lines.map((line, lineIndex) => {
      if (line.startsWith("### ")) {
        return <h4 key={`${index}-${lineIndex}`} className="font-semibold mt-2 mb-1">{line.slice(4)}</h4>;
      }
      if (line.startsWith("## ")) {
        return <h3 key={`${index}-${lineIndex}`} className="font-bold mt-2 mb-1">{line.slice(3)}</h3>;
      }
      if (line.startsWith("# ")) {
        return <h2 key={`${index}-${lineIndex}`} className="font-bold text-lg mt-2 mb-1">{line.slice(2)}</h2>;
      }
      
      if (line.match(/^[\-\*]\s/)) {
        const content = line.slice(2);
        return (
          <div key={`${index}-${lineIndex}`} className="flex gap-2 ml-2">
            <span>•</span>
            <span>{formatInline(content)}</span>
          </div>
        );
      }
      
      if (line.match(/^\d+\.\s/)) {
        const match = line.match(/^(\d+)\.\s(.*)$/);
        if (match) {
          return (
            <div key={`${index}-${lineIndex}`} className="flex gap-2 ml-2">
              <span>{match[1]}.</span>
              <span>{formatInline(match[2])}</span>
            </div>
          );
        }
      }
      
      if (line.trim() === "") {
        return <br key={`${index}-${lineIndex}`} />;
      }
      
      return <p key={`${index}-${lineIndex}`} className="mb-1">{formatInline(line)}</p>;
    });
  });
};

const formatInline = (text: string): React.ReactNode => {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;
  
  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);
    const codeMatch = remaining.match(/`([^`]+)`/);
    
    const matches = [
      boldMatch ? { type: "bold", match: boldMatch, index: remaining.indexOf(boldMatch[0]) } : null,
      codeMatch ? { type: "code", match: codeMatch, index: remaining.indexOf(codeMatch[0]) } : null,
    ].filter(Boolean).sort((a, b) => a!.index - b!.index);
    
    if (matches.length === 0) {
      parts.push(remaining);
      break;
    }
    
    const first = matches[0]!;
    
    if (first.index > 0) {
      parts.push(remaining.slice(0, first.index));
    }
    
    if (first.type === "bold") {
      parts.push(<strong key={key++}>{first.match![1]}</strong>);
    } else if (first.type === "code") {
      parts.push(<code key={key++} className="bg-background/50 px-1 rounded text-xs">{first.match![1]}</code>);
    }
    
    remaining = remaining.slice(first.index + first.match![0].length);
  }
  
  return parts.length === 1 ? parts[0] : parts;
};

export const AppAssistant = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [savedConversations, setSavedConversations] = useState<SavedConversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastUserIdRef = useRef<string | null>(null);

  // Reset chat on user change (login/logout)
  useEffect(() => {
    if (user?.id !== lastUserIdRef.current) {
      if (lastUserIdRef.current !== null) {
        setMessages([]);
        setCurrentConversationId(null);
      }
      lastUserIdRef.current = user?.id || null;
    }
  }, [user?.id]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input when opening
  useEffect(() => {
    if (isOpen && inputRef.current && !showHistory) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, showHistory]);

  // Load saved conversations
  const loadSavedConversations = useCallback(async () => {
    if (!user) return;
    setLoadingHistory(true);
    try {
      const { data, error } = await supabase
        .from('assistant_conversations')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });
      
      if (error) throw error;
      setSavedConversations((data || []).map(conv => ({
        ...conv,
        messages: (conv.messages as unknown as Message[]) || []
      })));
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, [user]);

  // Load conversations when opening history
  useEffect(() => {
    if (showHistory && user) {
      loadSavedConversations();
    }
  }, [showHistory, user, loadSavedConversations]);

  const saveConversation = async () => {
    if (!user || messages.length === 0) return;
    
    try {
      // Generate title from first user message
      const firstUserMessage = messages.find(m => m.role === "user");
      const title = firstUserMessage?.content.slice(0, 50) + (firstUserMessage && firstUserMessage.content.length > 50 ? "..." : "") || "Neuer Chat";
      
      if (currentConversationId) {
        // Update existing conversation
        const { error } = await supabase
          .from('assistant_conversations')
          .update({
            messages: JSON.parse(JSON.stringify(messages)),
            updated_at: new Date().toISOString()
          })
          .eq('id', currentConversationId);
        
        if (error) throw error;
        toast({ title: "Chat aktualisiert" });
      } else {
        // Create new conversation
        const { data, error } = await supabase
          .from('assistant_conversations')
          .insert([{
            user_id: user.id,
            title,
            messages: JSON.parse(JSON.stringify(messages))
          }])
          .select()
          .single();
        
        if (error) throw error;
        setCurrentConversationId(data.id);
        toast({ title: "Chat gespeichert" });
      }
    } catch (error) {
      console.error('Error saving conversation:', error);
      toast({ title: "Fehler beim Speichern", variant: "destructive" });
    }
  };

  const loadConversation = (conversation: SavedConversation) => {
    setMessages(conversation.messages);
    setCurrentConversationId(conversation.id);
    setShowHistory(false);
  };

  const deleteConversation = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const { error } = await supabase
        .from('assistant_conversations')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      setSavedConversations(prev => prev.filter(c => c.id !== id));
      if (currentConversationId === id) {
        setCurrentConversationId(null);
        setMessages([]);
      }
      toast({ title: "Chat gelöscht" });
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast({ title: "Fehler beim Löschen", variant: "destructive" });
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setCurrentConversationId(null);
    setShowHistory(false);
  };

  const sendMessage = useCallback(async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = { role: "user", content: input.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    let assistantContent = "";

    try {
      const response = await fetch(
        `https://puwkrtqzszjsjkxowvin.supabase.co/functions/v1/app-assistant`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InB1d2tydHF6c3pqc2preG93dmluIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTQwOTAyODIsImV4cCI6MjA2OTY2NjI4Mn0.fwAQ1FmbzQBnAmieAdM95-HA21iIm3jWVIcP58zMJxw`,
          },
          body: JSON.stringify({
            messages: [...messages, userMessage].map(m => ({
              role: m.role,
              content: m.content,
            })),
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to get response");
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No reader");

      const decoder = new TextDecoder();
      let buffer = "";

      setMessages(prev => [...prev, { role: "assistant", content: "" }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let newlineIndex;
        while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
          let line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") break;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              assistantContent += content;
              setMessages(prev => {
                const newMessages = [...prev];
                const lastIndex = newMessages.length - 1;
                if (newMessages[lastIndex]?.role === "assistant") {
                  newMessages[lastIndex] = {
                    ...newMessages[lastIndex],
                    content: assistantContent,
                  };
                }
                return newMessages;
              });
            }
          } catch {
            // Incomplete JSON, continue
          }
        }
      }
    } catch (error) {
      console.error("Assistant error:", error);
      setMessages(prev => [
        ...prev,
        {
          role: "assistant",
          content: "Entschuldigung, es gab einen Fehler. Bitte versuche es erneut.",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, messages]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Heute";
    if (diffDays === 1) return "Gestern";
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  return (
    <>
      {/* Chat Button */}
      <Button
        onClick={() => setIsOpen(true)}
        className={cn(
          "fixed bottom-4 right-4 z-50 h-12 w-12 rounded-full shadow-lg",
          "sm:bottom-6 sm:right-6 sm:h-14 sm:w-14",
          "bg-primary hover:bg-primary/90 text-primary-foreground",
          "transition-all duration-200 hover:scale-105",
          isOpen && "hidden"
        )}
      >
        <MessageCircle className="h-5 w-5 sm:h-6 sm:w-6" />
      </Button>

      {/* Chat Window */}
      {isOpen && (
        <div
          className={cn(
            "fixed z-50 bg-background border border-border shadow-2xl flex flex-col overflow-hidden rounded-2xl transition-all duration-200",
            isMaximized
              ? "inset-4 sm:inset-6"
              : "bottom-4 right-4 left-4 h-[60vh] sm:inset-auto sm:bottom-6 sm:right-6 sm:left-auto sm:w-[360px] sm:h-[500px] sm:max-h-[80vh]",
            "animate-in slide-in-from-bottom-4 fade-in duration-200"
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-primary text-primary-foreground rounded-t-2xl">
            <div className="flex items-center gap-2">
              {showHistory ? (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowHistory(false)}
                  className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              ) : (
                <Bot className="h-5 w-5" />
              )}
              <span className="font-semibold">{showHistory ? "Verlauf" : "D2D+ Assistent"}</span>
            </div>
            <div className="flex items-center gap-1">
              {!showHistory && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowHistory(true)}
                    className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
                    title="Verlauf"
                  >
                    <History className="h-4 w-4" />
                  </Button>
                  {messages.length > 0 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={saveConversation}
                      className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
                      title="Chat speichern"
                    >
                      <Save className="h-4 w-4" />
                    </Button>
                  )}
                </>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMaximized(!isMaximized)}
                className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
              >
                {isMaximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(false)}
                className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {showHistory ? (
            /* History View */
            <ScrollArea className="flex-1 p-4">
              {loadingHistory ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : savedConversations.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">Keine gespeicherten Chats</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2 mb-4"
                    onClick={startNewChat}
                  >
                    <MessageCircle className="h-4 w-4" />
                    Neuer Chat
                  </Button>
                  {savedConversations.map((conv) => (
                    <div
                      key={conv.id}
                      onClick={() => loadConversation(conv)}
                      className={cn(
                        "p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors",
                        currentConversationId === conv.id && "border-primary bg-primary/5"
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{conv.title}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDate(conv.updated_at)} • {conv.messages.length} Nachrichten
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                          onClick={(e) => deleteConversation(conv.id, e)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          ) : (
            /* Chat View */
            <>
              <ScrollArea className="flex-1 p-4" ref={scrollRef}>
                {messages.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    <Bot className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm font-medium">Hallo! 👋</p>
                    <p className="text-xs mt-1">
                      Frag mich alles zur D2D+ App!
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((msg, i) => (
                      <div
                        key={i}
                        className={cn(
                          "flex gap-2",
                          msg.role === "user" ? "justify-end" : "justify-start"
                        )}
                      >
                        {msg.role === "assistant" && (
                          <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Bot className="h-4 w-4 text-primary" />
                          </div>
                        )}
                        <div
                          className={cn(
                            "rounded-2xl px-3 py-2 text-sm",
                            isMaximized ? "max-w-[70%]" : "max-w-[80%]",
                            msg.role === "user"
                              ? "bg-primary text-primary-foreground rounded-br-md"
                              : "bg-muted rounded-bl-md"
                          )}
                        >
                          {msg.role === "assistant" ? (
                            <div className="prose-sm">{formatMessage(msg.content)}</div>
                          ) : (
                            <p className="whitespace-pre-wrap">{msg.content}</p>
                          )}
                        </div>
                        {msg.role === "user" && (
                          <div className="h-7 w-7 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                            <User className="h-4 w-4 text-primary-foreground" />
                          </div>
                        )}
                      </div>
                    ))}
                    {isLoading && messages[messages.length - 1]?.role === "user" && (
                      <div className="flex gap-2 justify-start">
                        <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center">
                          <Bot className="h-4 w-4 text-primary" />
                        </div>
                        <div className="bg-muted rounded-2xl rounded-bl-md px-3 py-2">
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </ScrollArea>

              {/* Input */}
              <div className="p-3 border-t bg-background">
                <div className="flex gap-2">
                  <Input
                    ref={inputRef}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Stelle eine Frage..."
                    disabled={isLoading}
                    className="flex-1 rounded-full bg-muted border-0 focus-visible:ring-1"
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={!input.trim() || isLoading}
                    size="icon"
                    className="h-10 w-10 rounded-full"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};